// Utility functions for role-based routing and access control

/**
 * Determine the appropriate dashboard path based on user permissions
 * @param {Array} userPermissions - Array of user permission names
 * @param {string} userRole - The user's role (fallback)
 * @returns {string} - The dashboard path
 */
export const getDashboardPath = (userPermissions = [], userRole = '') => {
  // For system roles, use original hardcoded routing
  if (isSystemRole(userRole)) {
    return getDashboardPathByRole(userRole);
  }

  // For custom roles, use permission-based routing
  if (!userPermissions || userPermissions.length === 0) {
    return getDashboardPathByRole(userRole);
  }

  // Permission-based routing - check what dashboards the user can access
  const canAccessAdmin = hasAnyPermission(userPermissions, [
    'users.view', 'users.create', 'system.roles', 'system.permissions',
    'recruiters.view', 'recruiters.create', 'payments.approve'
  ]);
  
  const canAccessFinancial = hasAnyPermission(userPermissions, [
    'payments.view', 'payments.create', 'payments.approve', 'reports.generate'
  ]);
  
  const canAccessRecruiter = hasAnyPermission(userPermissions, [
    'students.view_own'
  ]);

  // Priority: Admin > Financial > Recruiter > Default to Admin
  if (canAccessAdmin) return '/admin';
  if (canAccessFinancial) return '/financial';
  if (canAccessRecruiter) return '/recruiter';
  
  // Default to admin dashboard (permissions will control what they can see)
  return '/admin';
};

/**
 * Fallback function for role-based dashboard routing
 * @param {string} userRole - The user's role
 * @returns {string} - The dashboard path
 */
export const getDashboardPathByRole = (userRole) => {
  switch (userRole) {
    case 'admin':
      return '/admin';
    case 'financial_officer':
      return '/financial';
    case 'recruiter':
      return '/recruiter';
    default:
      // For custom roles, determine based on common role patterns
      if (userRole && userRole.includes('admin')) {
        return '/admin';
      } else if (userRole && userRole.includes('financial')) {
        return '/financial';
      } else if (userRole && userRole.includes('recruiter')) {
        return '/recruiter';
      }
      // Default to admin dashboard
      return '/admin';
  }
};

/**
 * Check if user can access a specific dashboard based on permissions
 * @param {Array} userPermissions - Array of user permission names
 * @param {string} dashboardType - The dashboard type ('admin', 'financial', 'recruiter')
 * @param {string} userRole - The user's role (fallback)
 * @returns {boolean} - Whether the user can access the dashboard
 */
export const canAccessDashboard = (userPermissions = [], dashboardType, userRole = '') => {
  // For system roles, use original hardcoded access control
  if (isSystemRole(userRole)) {
    return canAccessDashboardByRole(userRole, dashboardType);
  }

  // For custom roles, use permission-based access control
  if (!userPermissions || userPermissions.length === 0) {
    return canAccessDashboardByRole(userRole, dashboardType);
  }

  // Permission-based access control
  switch (dashboardType) {
    case 'admin':
      // Admin dashboard requires at least one admin-level permission
      return hasAnyPermission(userPermissions, [
        'users.view', 'users.create', 'users.edit', 'users.delete',
        'system.roles', 'system.permissions', 'system.settings', 'system.audit',
        'recruiters.view', 'recruiters.create', 'recruiters.edit', 'recruiters.delete',
        'payments.approve', 'dashboard.analytics'
      ]) || hasPermission(userPermissions, 'dashboard.view'); // Basic dashboard access
      
    case 'financial':
      // Financial dashboard requires financial permissions
      return hasAnyPermission(userPermissions, [
        'payments.view', 'payments.create', 'payments.edit', 'payments.upload', 'payments.approve',
        'reports.view', 'reports.generate', 'reports.export'
      ]);
      
    case 'recruiter':
      // Recruiter dashboard requires recruiter permissions
      return hasAnyPermission(userPermissions, [
        'students.view_own', 'students.add_admission', 'students.add_enrollment'
      ]);
      
    default:
      return false;
  }
};

/**
 * Fallback function for role-based dashboard access
 * @param {string} userRole - The user's role
 * @param {string} dashboardType - The dashboard type
 * @returns {boolean} - Whether the user can access the dashboard
 */
export const canAccessDashboardByRole = (userRole, dashboardType) => {
  switch (dashboardType) {
    case 'admin':
      return userRole === 'admin' || 
             (userRole && userRole.includes('admin'));
    case 'financial':
      return userRole === 'financial_officer' || 
             (userRole && userRole.includes('financial'));
    case 'recruiter':
      return userRole === 'recruiter' || 
             (userRole && userRole.includes('recruiter'));
    default:
      return false;
  }
};

/**
 * Check if a role is a system role
 * @param {string} role - The role to check
 * @returns {boolean} - Whether the role is a system role
 */
export const isSystemRole = (role) => {
  return ['admin', 'financial_officer', 'recruiter'].includes(role);
};

/**
 * Check if user has a specific permission
 * @param {Array} userPermissions - Array of user permission names
 * @param {string} permission - Permission to check
 * @returns {boolean} - Whether the user has the permission
 */
export const hasPermission = (userPermissions = [], permission) => {
  return userPermissions.includes(permission);
};

/**
 * Check if user has any of the specified permissions
 * @param {Array} userPermissions - Array of user permission names
 * @param {Array} permissions - Array of permissions to check
 * @returns {boolean} - Whether the user has any of the permissions
 */
export const hasAnyPermission = (userPermissions = [], permissions = []) => {
  return permissions.some(permission => userPermissions.includes(permission));
};

/**
 * Check if user has all of the specified permissions
 * @param {Array} userPermissions - Array of user permission names
 * @param {Array} permissions - Array of permissions to check
 * @returns {boolean} - Whether the user has all of the permissions
 */
export const hasAllPermissions = (userPermissions = [], permissions = []) => {
  return permissions.every(permission => userPermissions.includes(permission));
};

/**
 * Get role display information
 * @param {string} roleName - The role name
 * @returns {object} - Role display information with emoji, displayName, and badgeClass
 */
export const getRoleDisplay = (roleName) => {
  const getEmoji = (name) => {
    switch (name) {
      case 'admin': return '👑';
      case 'financial_officer': return '💼';
      case 'recruiter': return '👤';
      default: return '🔧';
    }
  };
  
  const getBadgeClass = (name) => {
    switch (name) {
      case 'admin': return 'badge-danger';
      case 'financial_officer': return 'badge-info';
      case 'recruiter': return 'badge-success';
      default: return 'badge-secondary';
    }
  };
  
  const getDisplayName = (name) => {
    switch (name) {
      case 'admin': return 'Administrator';
      case 'financial_officer': return 'Financial Officer';
      case 'recruiter': return 'Recruiter';
      default: return name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' ');
    }
  };
  
  return {
    emoji: getEmoji(roleName),
    displayName: getDisplayName(roleName),
    badgeClass: getBadgeClass(roleName)
  };
};